import {setPremoveEnabled} from "./game/premove.ts";
import {setMuted} from "./sound.ts";
import {setAnalysisDepth,type DepthId} from "./engine/analysisDepth.ts";
// chessground layout + piece set (self-contained data-URI assets — no external
// requests, which keeps us COEP-friendly). The board *squares* are painted by
// our own conic-gradient checker in `style.css` (see `boardTheme.ts`), so we
// deliberately drop chessground's brown square stylesheet.
//
// Order matters: chessground's base.css sets `cg-board { background-size: cover }`
// at the same specificity as our checker rule. Our `style.css` must load *after*
// it so our `25% 25%` wins the cascade — otherwise the whole board paints as a
// single stretched 2×2 tile (four giant squares instead of an 8×8 checker).
import "chessground/assets/chessground.base.css";
import "chessground/assets/chessground.cburnett.css";
import "./style.css";
import { errText } from "./dom.ts";
import { route, startRouter } from "./router.ts";
import { refreshUser } from "./session.ts";
import { applyBoardTheme, setBoardTheme } from "./ui/boardTheme.ts";
import { initTheme, setTheme } from "./theme.ts";
import { initLang } from "./i18n/index.ts";
import { initReadyBanner } from "./ui/readyBanner.ts";
import { initWaitingInvitations } from "./ui/waitingInvitations.ts";
import { initTurnAlerts } from "./ui/turnAlerts.ts";
import { initServerStatus } from "./ui/serverStatus.ts";
import { playPage, playScreen, playInvitationScreen, playSlotScreen } from "./screens/play.ts";
import { clubScreen } from "./screens/club.ts";
import { myGamesScreen } from "./screens/myGames.ts";
import { captureOwnerFragment, ownerImportScreen } from "./play/owner.ts";
import { inviteScreen } from "./screens/invite.ts";
import { loginScreen } from "./screens/login.ts";
import { forgotScreen } from "./screens/forgot.ts";
import { resetScreen } from "./screens/reset.ts";
import { onboardScreen } from "./screens/onboard.ts";
import { waitingScreen } from "./screens/waiting.ts";
import { gameScreen } from "./screens/game.ts";
import { computerScreen } from "./screens/computer.ts";
import { botGameScreen } from "./screens/botGame.ts";
import { analysisScreen, standaloneAnalysisScreen, studyGameScreen } from "./screens/analysis.ts";
import { settingsScreen } from "./screens/settings.ts";
import { studyScreen } from "./screens/study.ts";
import { openingCourseScreen } from "./screens/openings.ts";
import { collectionScreen, collectionGameScreen } from "./screens/collection.ts";
import { playersScreen } from "./screens/players.ts";
import { playerScreen } from "./screens/player.ts";
import { historyScreen } from "./screens/history.ts";
import { activityScreen } from "./screens/activity.ts";
import { adminScreen } from "./screens/admin.ts";
import { connectScreen } from "./screens/connect.ts";

// Surface every uncaught error / rejection as plain text in the console (Chromium
// otherwise shows a logged Error as an interactive block). Registered before any
// app code runs so nothing slips through.
window.addEventListener("error", (e) => {
  const detail = e.error ? errText(e.error) : `${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`;
  console.error(`[uncaught] ${detail}`);
});
window.addEventListener("unhandledrejection", (e) => {
  console.error(`[unhandledrejection] ${errText(e.reason)}`);
});

captureOwnerFragment();
route("/", playScreen);
route("/play", playScreen);
route("/play/invite/:token", playInvitationScreen);
route("/play/slot/:id", playSlotScreen);
route("/play/import/:id", ownerImportScreen);
route("/club", clubScreen);
route("/my-games", myGamesScreen);
route("/settings", () => settingsScreen());
route("/invite", inviteScreen);
route("/login", loginScreen);
route("/forgot", forgotScreen);
route("/reset/:token", resetScreen);
route("/i/:token", onboardScreen);
route("/wait/:token", waitingScreen);
route("/game/:id", gameScreen);
route("/game/bot/:id", botGameScreen);
route("/play/computer", computerScreen);
route("/analysis", standaloneAnalysisScreen);
route("/analysis/game/:id", (p) => analysisScreen("server", p.id));
route("/analysis/bot/:id", (p) => analysisScreen("bot", p.id));
route("/study", studyScreen);
route("/study/openings/:slug", (p) => openingCourseScreen(p.slug));
route("/study/openings/:slug/:side", (p) => openingCourseScreen(p.slug, p.side));
route("/study/game/:id", (p) => studyGameScreen(p.id));
route("/study/collection/:slug", (p) => collectionScreen(p.slug));
route("/study/collection/:slug/:idx", (p) => collectionGameScreen(p.slug, p.idx));
route("/players", playersScreen);
route("/player/:name", playerScreen);
route("/history", historyScreen);
route("/activity", activityScreen);
route("/admin", adminScreen);
route("/connect/telegram", connectScreen);
const oldInvitation = () => playPage('<h1>This link is from an older ChessME version</h1><p>Open the original Messages game and choose Continue live to create a current invitation.</p><a class="btn" data-link href="/">Start a new game</a>');
route("/continue/:token", oldInvitation);
route("/continuation/:id", oldInvitation);
route("/native-auth/:id", oldInvitation);

// Establish "am I signed in?" once, then start routing. The Ready-signal banner
// listens app-wide (UC-6), independent of the current screen.
(async () => {
  // Resolve language first (stamps <html lang>), so every screen renders in the
  // chosen catalog. Then appearance + board theme, so the very first render is
  // already themed (the no-flash script in index.html handled first paint; these
  // keep the DOM authoritative for the app's lifetime).
  initLang();
  initTheme();
  applyBoardTheme();
  await refreshUser();
  initReadyBanner();
  initWaitingInvitations();
  initTurnAlerts();
  initServerStatus();
  startRouter();
})();

// One application-level listener keeps existing preference helpers authoritative across tabs.
window.addEventListener("storage", event => {
  if (event.key === "board-theme") setBoardTheme(event.newValue ?? "green");
  if (event.key === "chess:theme") setTheme(event.newValue === "light" || event.newValue === "dark" ? event.newValue : "system");
  if (event.key === "chess:premove") setPremoveEnabled(event.newValue !== "0");
  if (event.key === "chess:muted") setMuted(event.newValue === "1");
  if (event.key === "chess:analysisDepth") setAnalysisDepth(["fast","deep","unlimited"].includes(event.newValue ?? "") ? event.newValue as DepthId : "balanced");
});
